import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {View, Text, StyleSheet, Platform} from 'react-native';
import Modal from 'react-native-modal';
import {WebView} from 'react-native-webview';
import type {WebViewMessageEvent, WebView as WebViewType} from 'react-native-webview';

import {APP_CONFIG} from '../config';
import {theme} from '../constants';
import Button from './Button';
import type {Coordinate} from '../types';
import {calculateDistanceKm} from '../utils/location';

type Props = {
  visible: boolean;
  value?: Coordinate;
  onClose: () => void;
  onConfirm: (coordinate: Coordinate) => void;
};

const LocationPickerModal: React.FC<Props> = ({visible, value, onClose, onConfirm}) => {
  const {location, maps} = APP_CONFIG;

  const centerCoordinate = useMemo<Coordinate>(
    () => ({latitude: location.latitude, longitude: location.longitude}),
    [location.latitude, location.longitude],
  );
  const initialCoordinate = useMemo<Coordinate>(
    () => value ?? centerCoordinate,
    [value, centerCoordinate],
  );

  const [selectedCoordinate, setSelectedCoordinate] = useState<Coordinate>(initialCoordinate);
  const [displayCoordinate, setDisplayCoordinate] = useState<Coordinate>(initialCoordinate);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSelectionValid, setIsSelectionValid] = useState(true);
  const [currentLocationError, setCurrentLocationError] = useState<string | null>(null);
  const [isFetchingCurrentLocation, setIsFetchingCurrentLocation] = useState(false);
  const isWeb = Platform.OS === 'web';
  const hasApiKey = Boolean((maps?.googleApiKey ?? '').trim());
  const webMapContainerRef = useRef<any>(null);
  const webMapInstanceRef = useRef<any>(null);
  const webMarkerRef = useRef<any>(null);
  const webViewRef = useRef<WebViewType>(null);
  
  useEffect(() => {
    if (visible) {
      setSelectedCoordinate(initialCoordinate);
      setDisplayCoordinate(initialCoordinate);
      setErrorMessage(null);
      setIsSelectionValid(true);
      setCurrentLocationError(null);
    }
  }, [visible, initialCoordinate]);

  const handleCoordinateSelection = useCallback(
    (coordinate: Coordinate): boolean => {
      setCurrentLocationError(null);
      setDisplayCoordinate(coordinate);
      const distanceKm = calculateDistanceKm(centerCoordinate, coordinate);

      if (distanceKm > location.selectionRadiusKm) {
        setErrorMessage('Selected location is outside of our delivery area.');
        setIsSelectionValid(false);
        return false;
      }

      setSelectedCoordinate(coordinate);
      setErrorMessage(null);
      setIsSelectionValid(true);
      return true;
    },
    [centerCoordinate, location.selectionRadiusKm],
  );

  const requestCurrentLocation = useCallback(async () => {
    setCurrentLocationError(null);
    setIsFetchingCurrentLocation(true);

    try {
      const geolocation = (
        globalThis as {
          navigator?: {geolocation?: {getCurrentPosition?: (...args: any[]) => void}};
        }
      ).navigator?.geolocation;

      if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
        throw new Error('Geolocation is not supported on this device.');
      }

      const getCurrentPosition = geolocation.getCurrentPosition.bind(geolocation);

      const coordinate = await new Promise<Coordinate>((resolve, reject) => {
        getCurrentPosition(
          (position: any) => {
            if (position?.coords) {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            } else {
              reject(new Error('Unable to read current location.'));
            }
          },
          (error: any) => {
            if (error?.message) {
              reject(new Error(error.message));
            } else {
              reject(new Error('Unable to fetch current location.'));
            }
          },
          {enableHighAccuracy: true, timeout: 10000},
        );
      });

      handleCoordinateSelection(coordinate);
    } catch (error: any) {
      setCurrentLocationError(
        error?.message ?? 'Unable to fetch current location. Please try again.',
      );
    } finally {
      setIsFetchingCurrentLocation(false);
    }
  }, [handleCoordinateSelection]);

  const handleConfirm = () => {
    if (!isSelectionValid) {
      return;
    }
    onConfirm(selectedCoordinate);
  };

  const mapHtml = useMemo(() => {
    const {latitude, longitude} = initialCoordinate;
    const apiKey = hasApiKey ? maps?.googleApiKey ?? '' : '';

    return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body, #map {
        height: 100%;
        margin: 0;
        padding: 0;
      }
    </style>
    <script>
      let marker;
      let map;
      function sendCoordinates(position) {
        if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            latitude: position.lat(),
            longitude: position.lng()
          }));
        }
      }

      function initMap() {
        const center = { lat: ${latitude}, lng: ${longitude} };
        map = new google.maps.Map(document.getElementById('map'), {
          center,
          zoom: 14,
          disableDefaultUI: true,
          disableDoubleClickZoom: true
        });

        marker = new google.maps.Marker({
          position: center,
          map,
          draggable: true
        });

        map.addListener('click', function(event) {
          marker.setPosition(event.latLng);
          sendCoordinates(event.latLng);
        });

        map.addListener('dblclick', function(event) {
          if (!event || !event.latLng) {
            return;
          }

          marker.setPosition(event.latLng);
          sendCoordinates(event.latLng);
        });

        marker.addListener('dragend', function(event) {
          sendCoordinates(event.latLng);
        });

        sendCoordinates(marker.getPosition());
      }

      window.updateMarkerPosition = function(lat, lng) {
        if (!marker || !map) {
          return;
        }

        const nextPosition = new google.maps.LatLng(lat, lng);
        marker.setPosition(nextPosition);
        map.panTo(nextPosition);
      };

      window.initMap = initMap;
    </script>
  </head>
  <body>
    <div id="map"></div>
    <script src="https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap" async defer></script>
  </body>
</html>`;
  }, [initialCoordinate, hasApiKey, maps?.googleApiKey]);

  const handleWebViewMessage = (event: WebViewMessageEvent) => {
    try {
      const data = JSON.parse(event.nativeEvent.data ?? '{}');
      if (
        typeof data.latitude === 'number' &&
        typeof data.longitude === 'number'
      ) {
        handleCoordinateSelection({
          latitude: data.latitude,
          longitude: data.longitude,
        });
      }
    } catch (error) {
      // Ignore malformed messages
    }
  };

  useEffect(() => {
    if (!visible || !isWeb || !hasApiKey) {
      return;
    }

    const container = webMapContainerRef.current as unknown as HTMLDivElement | null;
    if (!container || typeof document === 'undefined') {
      return;
    }

    const listeners: Array<{remove: () => void}> = [];

    const cleanupListeners = () => {
      while (listeners.length) {
        const listener = listeners.pop();
        listener?.remove();
      }
    };

    const detachMap = () => {
      cleanupListeners();
      webMarkerRef.current?.setMap(null);
      webMapInstanceRef.current = null;
      webMarkerRef.current = null;
      container.innerHTML = '';
    };

    const attachListeners = (googleMaps: any) => {
      if (webMapInstanceRef.current) {
        const mapClickListener = googleMaps.event.addListener(
          webMapInstanceRef.current,
          'click',
          (event: any) => {
            if (!event?.latLng) {
              return;
            }

            const coordinate = {
              latitude: event.latLng.lat(),
              longitude: event.latLng.lng(),
            };

            webMarkerRef.current?.setPosition(event.latLng);
            handleCoordinateSelection(coordinate);
          },
        );
        listeners.push({remove: () => mapClickListener.remove()});

        const mapDoubleClickListener = googleMaps.event.addListener(
          webMapInstanceRef.current,
          'dblclick',
          (event: any) => {
            if (!event?.latLng) {
              return;
            }

            const coordinate = {
              latitude: event.latLng.lat(),
              longitude: event.latLng.lng(),
            };

            webMarkerRef.current?.setPosition(event.latLng);
            handleCoordinateSelection(coordinate);
          },
        );
        listeners.push({remove: () => mapDoubleClickListener.remove()});
      }

      if (webMarkerRef.current) {
        const dragListener = googleMaps.event.addListener(
          webMarkerRef.current,
          'dragend',
          (event: any) => {
            if (!event?.latLng) {
              return;
            }

            const coordinate = {
              latitude: event.latLng.lat(),
              longitude: event.latLng.lng(),
            };

            handleCoordinateSelection(coordinate);
          },
        );
        listeners.push({remove: () => dragListener.remove()});
      }
    };

    const initializeMap = () => {
      const googleMaps = (window as any).google?.maps;
      if (!googleMaps || !container) {
        return;
      }

      const center = new googleMaps.LatLng(
        initialCoordinate.latitude,
        initialCoordinate.longitude,
      );

      const map = new googleMaps.Map(container, {
        center,
        zoom: 14,
        disableDefaultUI: true,
        disableDoubleClickZoom: true,
      });

      const marker = new googleMaps.Marker({
        position: center,
        map,
        draggable: true,
      });

      webMapInstanceRef.current = map;
      webMarkerRef.current = marker;

      attachListeners(googleMaps);
    };

    const googleMaps = (window as any).google?.maps;
    if (googleMaps) {
      initializeMap();
    } else {
      const existingScript = document.querySelector<HTMLScriptElement>(
        'script[data-google-maps-picker]'
      );

      const handleScriptLoad = () => {
        initializeMap();
      };

      if (existingScript) {
        existingScript.addEventListener('load', handleScriptLoad);
        listeners.push({
          remove: () => existingScript.removeEventListener('load', handleScriptLoad),
        });
      } else {
        const script = document.createElement('script');
        script.async = true;
        script.defer = true;
        script.dataset.googleMapsPicker = 'true';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${maps?.googleApiKey ?? ''}`;
        script.addEventListener('load', handleScriptLoad);
        document.body.appendChild(script);
        listeners.push({remove: () => script.removeEventListener('load', handleScriptLoad)});
      }
    }

    return () => {
      detachMap();
      cleanupListeners();
    };
  }, [
    visible,
    isWeb,
    hasApiKey,
    handleCoordinateSelection,
    initialCoordinate.latitude,
    initialCoordinate.longitude,
    maps?.googleApiKey,
  ]);

  useEffect(() => {
    if (!isWeb || !hasApiKey) {
      return;
    }

    const googleMaps = (window as any).google?.maps;
    if (!googleMaps || !webMarkerRef.current || !webMapInstanceRef.current) {
      return;
    }

    const latLng = new googleMaps.LatLng(
      displayCoordinate.latitude,
      displayCoordinate.longitude,
    );

    webMarkerRef.current.setPosition(latLng);
    webMapInstanceRef.current.panTo(latLng);
  }, [isWeb, hasApiKey, displayCoordinate]);

  useEffect(() => {
    if (isWeb || !hasApiKey || !visible) {
      return;
    }

    const script = `
      if (window.updateMarkerPosition) {
        window.updateMarkerPosition(${displayCoordinate.latitude}, ${displayCoordinate.longitude});
      }
      true;
    `;

    webViewRef.current?.injectJavaScript(script);
  }, [displayCoordinate, isWeb, hasApiKey, visible]);

  return (
    <Modal
      isVisible={visible}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      style={styles.modal}
      backdropTransitionOutTiming={0}
      useNativeDriver={false}
    >
      <View style={styles.container}>
        <Text style={styles.title}>Select delivery location</Text>
        <Text style={styles.description}>
          Tap on the map or use your current location to select a delivery address within our
          service area.
        </Text>
        <View style={styles.mapContainer}>
          {!hasApiKey ? (
            <View style={styles.mapFallback}>
              <Text style={styles.mapFallbackText}>
                Google Maps is unavailable because the API key is missing.
              </Text>
            </View>
          ) : isWeb ? (
            <View ref={webMapContainerRef} style={styles.webMap} />
          ) : (
            <WebView
              ref={webViewRef}
              originWhitelist={['*']}
              style={styles.webView}
              source={{html: mapHtml}}
              onMessage={handleWebViewMessage}
              javaScriptEnabled
            />
          )}
        </View>
        <Button
          title='Use my current location'
          onPress={requestCurrentLocation}
          loading={isFetchingCurrentLocation}
          containerStyle={styles.currentLocationButton}
        />
        <View style={styles.infoContainer}>
          <Text style={styles.infoLabel}>Selected coordinates</Text>
          <Text style={styles.infoValue}>
            {displayCoordinate.latitude.toFixed(5)}, {displayCoordinate.longitude.toFixed(5)}
          </Text>
        </View>
        {currentLocationError && <Text style={styles.errorText}>{currentLocationError}</Text>}
        {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}
        <Button
          title='Confirm location'
          onPress={handleConfirm}
          disabled={!isSelectionValid}
        />
        <Button
          title='Cancel'
          onPress={onClose}
          transparent
          containerStyle={styles.cancelButton}
        />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modal: {
    margin: 20,
    justifyContent: 'center',
  },
  container: {
    padding: 20,
    borderRadius: 16,
    backgroundColor: theme.colors.backgroundPrimary,
  },
  title: {
    ...theme.fonts.DMSans_700Bold,
    fontSize: 18,
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  description: {
    ...theme.fonts.DMSans_400Regular,
    fontSize: 13,
    color: theme.colors.textMuted,
    marginBottom: 16,
  },
  mapContainer: {
    height: 300,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
    position: 'relative',
  },
  currentLocationButton: {
    marginBottom: 12,
  },
  webView: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  webMap: {
    flex: 1,
  },
  mapFallback: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  mapFallbackText: {
    ...theme.fonts.DMSans_500Medium,
    fontSize: 12,
    color: theme.colors.textMuted,
    textAlign: 'center',
  },
  infoContainer: {
    marginBottom: 12,
  },
  infoLabel: {
    ...theme.fonts.DMSans_500Medium,
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 4,
  },
  infoValue: {
    ...theme.fonts.DMSans_700Bold,
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  errorText: {
    ...theme.fonts.DMSans_500Medium,
    fontSize: 12,
    color: '#FF5A5F',
    marginBottom: 12,
  },
  cancelButton: {
    marginTop: 10,
  },
});

export type {Coordinate};
export default LocationPickerModal;
